import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'
import { SHIPPING_ENDPOINTS } from './shipping.endpoints'
import type { CreateShippingAddressInput, ShippingAddress, UpdateShippingAddressInput } from './shipping.types'

// --- Service Functions ---

export const getShippingAddressesService = async () => {
  const { data } = await apiClient.get<ShippingAddress[]>(SHIPPING_ENDPOINTS.LIST)
  return data
}

export const createShippingAddressService = async (payload: CreateShippingAddressInput) => {
  const { data } = await apiClient.post<ShippingAddress>(SHIPPING_ENDPOINTS.CREATE, payload)
  return data
}

export const updateShippingAddressService = async ({ shipping_slug, ...payload }: UpdateShippingAddressInput) => {
  const { data } = await apiClient.patch<ShippingAddress>(SHIPPING_ENDPOINTS.UPDATE(shipping_slug), payload)
  return data
}

export const deleteShippingAddressService = async (slug: string) => {
  const { data } = await apiClient.delete<{ message: string }>(SHIPPING_ENDPOINTS.DELETE(slug))
  return data
}

export const setShippingAddressDefaultService = async (slug: string) => {
  const { data } = await apiClient.patch<{ message: string }>(SHIPPING_ENDPOINTS.SET_DEFAULT(slug))
  return data
}

// --- Query & Mutation Hooks ---

export const SHIPPING_QUERY_KEYS = {
  LIST: ['shipping-addresses'] as const,
  DETAIL: (slug: string) => ['shipping-address', slug] as const,
}

export const getShippingAddressBySlugService = async (slug: string) => {
  const { data } = await apiClient.get<ShippingAddress>(SHIPPING_ENDPOINTS.GET(slug))
  return data
}

import { useAuthStore } from '../auth/auth.store'

export const useShippingAddresses = () => {
  const { customer } = useAuthStore()
  return useQuery({
    queryKey: SHIPPING_QUERY_KEYS.LIST,
    queryFn: getShippingAddressesService,
    enabled: !!customer,
  })
}

export const useShippingAddress = (slug: string) => {
  return useQuery({
    queryKey: SHIPPING_QUERY_KEYS.DETAIL(slug),
    queryFn: () => getShippingAddressBySlugService(slug),
    enabled: !!slug,
  })
}

export const useCreateShippingAddress = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createShippingAddressService,
    onSuccess: (newAddress) => {
      if (newAddress) {
        queryClient.setQueryData(SHIPPING_QUERY_KEYS.LIST, (old: any) => {
          if (!old) return old
          if (Array.isArray(old)) {
            const list = newAddress.is_default ? old.map((a: ShippingAddress) => ({ ...a, is_default: false })) : old
            return [newAddress, ...list]
          }
          if (old.results && Array.isArray(old.results)) {
            const list = newAddress.is_default
              ? old.results.map((a: ShippingAddress) => ({ ...a, is_default: false }))
              : old.results
            return { ...old, results: [newAddress, ...list] }
          }
          return old
        })
      }
      queryClient.invalidateQueries({ queryKey: SHIPPING_QUERY_KEYS.LIST, refetchType: 'all' })
    },
  })
}

export const useUpdateShippingAddress = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateShippingAddressService,
    onSuccess: (updatedAddress, variables) => {
      const targetSlug = variables.shipping_slug || updatedAddress?.shipping_slug

      // Optimistically update the list in cache
      if (updatedAddress) {
        queryClient.setQueryData(SHIPPING_QUERY_KEYS.LIST, (old: any) => {
          if (!old) return old
          const updateItem = (item: ShippingAddress) => {
            if (item.shipping_slug === targetSlug) {
              return { ...item, ...updatedAddress }
            }
            if (updatedAddress.is_default) {
              return { ...item, is_default: false }
            }
            return item
          }

          if (Array.isArray(old)) {
            return old.map(updateItem)
          }
          if (old.results && Array.isArray(old.results)) {
            return {
              ...old,
              results: old.results.map(updateItem),
            }
          }
          return old
        })
      }

      // Update individual address cache if present
      if (targetSlug) {
        queryClient.setQueryData(SHIPPING_QUERY_KEYS.DETAIL(targetSlug), (old: any) =>
          updatedAddress ? { ...old, ...updatedAddress } : old,
        )
        queryClient.invalidateQueries({ queryKey: SHIPPING_QUERY_KEYS.DETAIL(targetSlug) })
      }

      // Invalidate and refetch the full list query
      queryClient.invalidateQueries({
        queryKey: SHIPPING_QUERY_KEYS.LIST,
        refetchType: 'all',
      })
    },
  })
}

export const useDeleteShippingAddress = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteShippingAddressService,
    onSuccess: (_data, slug) => {
      queryClient.setQueryData(SHIPPING_QUERY_KEYS.LIST, (old: any) => {
        if (!old) return old
        if (Array.isArray(old)) {
          return old.filter((item: ShippingAddress) => item.shipping_slug !== slug)
        }
        if (old.results && Array.isArray(old.results)) {
          return {
            ...old,
            results: old.results.filter((item: ShippingAddress) => item.shipping_slug !== slug),
          }
        }
        return old
      })
      queryClient.removeQueries({ queryKey: SHIPPING_QUERY_KEYS.DETAIL(slug) })
      queryClient.invalidateQueries({ queryKey: SHIPPING_QUERY_KEYS.LIST, refetchType: 'all' })
    },
  })
}

export const useSetShippingAddressDefault = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: setShippingAddressDefaultService,
    onSuccess: (_data, slug) => {
      queryClient.setQueryData(SHIPPING_QUERY_KEYS.LIST, (old: any) => {
        if (!old) return old
        if (Array.isArray(old)) {
          return old.map((item: ShippingAddress) => ({
            ...item,
            is_default: item.shipping_slug === slug,
          }))
        }
        if (old.results && Array.isArray(old.results)) {
          return {
            ...old,
            results: old.results.map((item: ShippingAddress) => ({
              ...item,
              is_default: item.shipping_slug === slug,
            })),
          }
        }
        return old
      })
      queryClient.invalidateQueries({ queryKey: SHIPPING_QUERY_KEYS.LIST, refetchType: 'all' })
    },
  })
}
